import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchDashboardSummary, fetchBetScores, fetchPositions, fetchDailyPnL, fetchOverview } from '../api/client';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DataFreshnessPanel } from '../components/FreshnessIndicator';
import QuotaWidget, { QuotaWidgetCompact } from '../components/QuotaWidget';
import PolymarketWidget, { PolymarketWidgetCompact } from '../components/PolymarketWidget';

export default function Dashboard() {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: fetchDashboardSummary,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: betScores } = useQuery({
    queryKey: ['bet-scores', { limit: 5, minScore: 40 }],
    queryFn: () => fetchBetScores({ limit: 5, minScore: 40 }),
  });

  const { data: positions } = useQuery({
    queryKey: ['positions'],
    queryFn: fetchPositions,
  });

  const { data: pnlData } = useQuery({
    queryKey: ['pnl-daily', 7],
    queryFn: () => fetchDailyPnL(7),
  });

  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: fetchOverview,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header with System Status */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="flex items-center gap-4">
          <QuotaWidgetCompact />
          <PolymarketWidgetCompact />
          <DataFreshnessPanel
            sources={[
              { label: 'Odds', lastUpdate: overview?.lastUpdates.odds },
              { label: 'Polymarket', lastUpdate: overview?.lastUpdates.polymarket },
            ]}
            warningThreshold={360}
            staleThreshold={600}
          />
          <SystemStatusBadge
            status={summary?.systemStatus || 'ACTIVE'}
            reason={summary?.statusReason}
          />
        </div>
      </div>

      {/* TWO KEY METRICS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bet Score Gauge */}
        <ScoreGauge
          title="Best Bet Score"
          score={summary?.betScore?.score || 0}
          recommendation={summary?.betScore?.recommendation || 'WAIT'}
          subtitle={summary?.betScore?.event
            ? `${summary.betScore.event.homeTeam} vs ${summary.betScore.event.awayTeam}`
            : 'No opportunities'}
          direction={summary?.betScore?.direction}
          thresholds={{ bet: 75, consider: 60, wait: 40 }}
          color="blue"
        />

        {/* Portfolio Health Gauge */}
        <ScoreGauge
          title="Portfolio Health"
          score={summary?.portfolioHealth?.score || 100}
          recommendation={
            (summary?.portfolioHealth?.score || 100) >= 70 ? 'HOLD' :
            (summary?.portfolioHealth?.score || 100) >= 50 ? 'MONITOR' :
            (summary?.portfolioHealth?.score || 100) >= 30 ? 'REDUCE' : 'EXIT'
          }
          subtitle={summary?.portfolioHealth?.positionCount
            ? `${summary.portfolioHealth.positionCount} position(s), ${formatCurrency(summary.portfolioHealth.totalUnrealizedPnL)} unrealized`
            : 'No open positions'}
          thresholds={{ hold: 70, monitor: 50, reduce: 30 }}
          color="green"
          warning={summary?.portfolioHealth?.atRiskCount
            ? `${summary.portfolioHealth.atRiskCount} at risk`
            : undefined}
        />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStat
          label="Today's P&L"
          value={formatCurrency(summary?.dailyPnL || 0)}
          positive={(summary?.dailyPnL || 0) >= 0}
        />
        <QuickStat
          label="Weekly Win Rate"
          value={summary?.weeklyWinRate != null
            ? `${(summary.weeklyWinRate * 100).toFixed(0)}%`
            : 'N/A'}
        />
        <QuickStat
          label="Actionable Bets"
          value={betScores?.summary.betCount.toString() || '0'}
          subtitle={`${betScores?.summary.considerCount || 0} more to review`}
        />
        <QuickStat
          label="Positions at Risk"
          value={summary?.portfolioHealth?.atRiskCount?.toString() || '0'}
          warning={(summary?.portfolioHealth?.atRiskCount || 0) > 0}
        />
      </div>

      {/* Event Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStat
          label="Scheduled Events"
          value={overview?.metrics?.totalEvents?.toString() || '0'}
          subtitle="From The Odds API"
        />
        <QuickStat
          label="Matched Events"
          value={overview?.metrics?.matchedEvents?.toString() || '0'}
          subtitle={`${overview?.metrics?.totalEvents ? Math.round((overview.metrics.matchedEvents / overview.metrics.totalEvents) * 100) : 0}% match rate`}
        />
        <QuickStat
          label="Active Signals"
          value={overview?.metrics?.activeSignals?.toString() || '0'}
          subtitle="RLM detected"
        />
        <QuickStat
          label="Detected Edges"
          value={overview?.metrics?.activeOpportunities?.toString() || '0'}
          subtitle="All confidence levels"
        />
      </div>

      {/* Action Recommendations */}
      {summary?.actions && (
        <div className="card bg-surface-50">
          <h2 className="text-lg font-semibold mb-3">Recommended Actions</h2>
          <div className="flex flex-wrap gap-3">
            {summary.actions.shouldBet && summary.actions.bestBetEvent && (
              <ActionBadge
                type="bet"
                text={`Place bet on ${summary.actions.bestBetEvent}`}
              />
            )}
            {summary.actions.shouldMonitor && (
              <ActionBadge
                type="monitor"
                text={`Monitor ${summary.portfolioHealth?.atRiskCount} position(s)`}
              />
            )}
            {!summary.actions.shouldBet && !summary.actions.shouldMonitor && (
              <ActionBadge
                type="wait"
                text="No immediate action needed"
              />
            )}
          </div>
        </div>
      )}

      {/* Two Columns: Opportunities & Positions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Bet Opportunities */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Top Opportunities</h2>
            <Link to="/opportunities" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {(!betScores?.scores || betScores.scores.length === 0) ? (
              <p className="text-gray-400 text-center py-4">No opportunities above threshold</p>
            ) : (
              betScores.scores.slice(0, 5).map((score) => (
                <Link
                  key={score.eventId}
                  to={`/events/${score.eventId}`}
                  className="flex items-center justify-between p-3 bg-surface-50 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ScoreBadge score={score.betScore} recommendation={score.recommendation} />
                    <div>
                      <div className="font-medium text-sm">
                        {score.homeTeam} vs {score.awayTeam}
                      </div>
                      <div className="text-xs text-gray-400">
                        {score.direction} • {score.hoursToEvent.toFixed(1)}h to event
                      </div>
                    </div>
                  </div>
                  <RecommendationBadge recommendation={score.recommendation} />
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Open Positions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Open Positions</h2>
            <Link to="/bets" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {(!positions?.positions || positions.positions.length === 0) ? (
              <p className="text-gray-400 text-center py-4">No open positions</p>
            ) : (
              positions.positions.slice(0, 5).map((position, index) => (
                <Link
                  key={position.eventId + index}
                  to={`/events/${position.eventId}`}
                  className="flex items-center justify-between p-3 bg-surface-50 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <HealthBadge health={position.health} status={position.healthStatus} />
                    <div>
                      <div className="font-medium text-sm">
                        {position.homeTeam} vs {position.awayTeam}
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatCurrency(position.unrealizedPnL)} unrealized
                      </div>
                    </div>
                  </div>
                  <RecommendationBadge
                    recommendation={position.recommendation}
                    variant="health"
                  />
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row: P&L Chart and System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 7-Day P&L Chart */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">7-Day P&L</h2>
          <div className="h-48">
            {pnlData?.data && pnlData.data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pnlData.data}>
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short' })}
                  />
                  <YAxis stroke="#6b7280" tickFormatter={(val) => `$${val}`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'P&L']}
                  />
                  <Line
                    type="monotone"
                    dataKey="pnl"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No P&L data available
              </div>
            )}
          </div>
        </div>

        {/* System Health - API Monitoring */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuotaWidget />
          <PolymarketWidget />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTS
// =============================================================================

function SystemStatusBadge({ status, reason }: { status: 'ACTIVE' | 'CAUTION' | 'PAUSE'; reason?: string }) {
  const colors = {
    ACTIVE: 'bg-success/20 text-success border-success/30',
    CAUTION: 'bg-warning/20 text-warning border-warning/30',
    PAUSE: 'bg-danger/20 text-danger border-danger/30',
  };

  return (
    <div className={`px-3 py-1 rounded-full border text-sm font-medium ${colors[status]}`} title={reason}>
      {status === 'ACTIVE' && '● '}
      {status === 'CAUTION' && '⚠ '}
      {status === 'PAUSE' && '⏸ '}
      {status}
    </div>
  );
}

function ScoreGauge({
  title,
  score,
  recommendation,
  subtitle,
  direction,
  thresholds,
  color,
  warning,
}: {
  title: string;
  score: number;
  recommendation: string;
  subtitle: string;
  direction?: string | null;
  thresholds: Record<string, number>;
  color: 'blue' | 'green';
  warning?: string;
}) {
  const getScoreColor = () => {
    if (color === 'blue') {
      if (score >= 75) return 'text-success';
      if (score >= 60) return 'text-primary';
      if (score >= 40) return 'text-warning';
      return 'text-gray-400';
    } else {
      if (score >= 70) return 'text-success';
      if (score >= 50) return 'text-warning';
      if (score >= 30) return 'text-orange-500';
      return 'text-danger';
    }
  };

  return (
    <div className="card text-center">
      <h3 className="text-sm text-gray-400 mb-2">{title}</h3>
      <div className={`text-5xl font-bold ${getScoreColor()}`}>
        {score}
      </div>
      <div className="text-lg font-medium mt-1">{recommendation}</div>
      <div className="text-sm text-gray-400 mt-2">{subtitle}</div>
      {direction && (
        <div className="text-xs text-primary mt-1">Direction: {direction}</div>
      )}
      {warning && (
        <div className="text-xs text-warning mt-2">⚠ {warning}</div>
      )}

      {/* Mini threshold legend */}
      <div className="flex justify-center gap-4 mt-4 text-xs text-gray-500">
        {Object.entries(thresholds).map(([label, value]) => (
          <span key={label}>{label}: {value}+</span>
        ))}
      </div>
    </div>
  );
}

function QuickStat({
  label,
  value,
  subtitle,
  positive,
  warning,
}: {
  label: string;
  value: string;
  subtitle?: string;
  positive?: boolean;
  warning?: boolean;
}) {
  return (
    <div className="card p-4">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`text-xl font-bold ${
        warning ? 'text-warning' :
        positive === true ? 'text-success' :
        positive === false ? 'text-danger' :
        'text-white'
      }`}>
        {value}
      </div>
      {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
    </div>
  );
}

function ActionBadge({ type, text }: { type: 'bet' | 'monitor' | 'wait'; text: string }) {
  const styles = {
    bet: 'bg-success/20 text-success border-success/30',
    monitor: 'bg-warning/20 text-warning border-warning/30',
    wait: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  const icons = {
    bet: '💰',
    monitor: '👁',
    wait: '⏳',
  };

  return (
    <div className={`px-3 py-2 rounded-lg border text-sm ${styles[type]}`}>
      {icons[type]} {text}
    </div>
  );
}

function ScoreBadge({ score, recommendation }: { score: number; recommendation: string }) {
  const colors = {
    BET: 'bg-success text-white',
    CONSIDER: 'bg-primary text-white',
    WAIT: 'bg-warning text-black',
    AVOID: 'bg-gray-600 text-white',
  };

  return (
    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold ${colors[recommendation as keyof typeof colors] || colors.WAIT}`}>
      {score}
    </div>
  );
}

function HealthBadge({ health, status }: { health: number; status: string }) {
  const colors = {
    healthy: 'bg-success text-white',
    watch: 'bg-warning text-black',
    reduce: 'bg-orange-500 text-white',
    exit: 'bg-danger text-white',
  };

  return (
    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold ${colors[status as keyof typeof colors] || colors.watch}`}>
      {health}
    </div>
  );
}

function RecommendationBadge({
  recommendation,
  variant = 'score',
}: {
  recommendation: string;
  variant?: 'score' | 'health';
}) {
  const scoreColors = {
    BET: 'text-success',
    CONSIDER: 'text-primary',
    WAIT: 'text-warning',
    AVOID: 'text-gray-400',
  };

  const healthColors = {
    HOLD: 'text-success',
    MONITOR: 'text-warning',
    REDUCE: 'text-orange-500',
    EXIT: 'text-danger',
  };

  const colors = variant === 'score' ? scoreColors : healthColors;

  return (
    <span className={`text-sm font-medium ${colors[recommendation as keyof typeof colors] || 'text-gray-400'}`}>
      {recommendation}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 bg-surface-50 rounded"></div>
      <div className="grid grid-cols-2 gap-6">
        <div className="h-48 bg-surface-50 rounded-lg"></div>
        <div className="h-48 bg-surface-50 rounded-lg"></div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 bg-surface-50 rounded-lg"></div>
        ))}
      </div>
    </div>
  );
}

function formatCurrency(value: number): string {
  const prefix = value >= 0 ? '+$' : '-$';
  return `${prefix}${Math.abs(value).toFixed(2)}`;
}
